<template>
  <template v-if="file.content">
    <CButton v-if="file.type === MediaFileType.IMAGE && file.content.extraSmall" theme="ghost-secondary" @click="openImage" class="c-media-file c-media-file-image">
      <img :src="file.content.extraSmall" :alt="file.description" class="c-media-file-thumbnail">
    </CButton>

    <div v-else-if="file.type === MediaFileType.VIDEO" class="c-media-file c-media-file-video">
      <video ref="playerEl" :src="file.content.extraSmall" controls class="js-player"/>
    </div>

    <div v-else-if="file.type === MediaFileType.AUDIO" class="c-media-file c-media-file-audio">
      <audio ref="playerEl" :src="file.content.extraSmall" controls/>
    </div>

    <CButton v-else-if="file.type === MediaFileType.PDF" :href="file.content.original ?? undefined" theme="ghost-secondary" class="c-media-file c-media-file-pdf">
      <img v-if="file.content.extraSmall" :src="file.content.extraSmall" :alt="file.description" class="c-media-file-thumbnail">
      <CIcon v-else name="pdf" :title="$t('media.pdfTitle')" :size="32"/>
    </CButton>
  </template>

  <template v-else>
    <div class="c-media-file c-media-file-placeholder">
      <CLoader :size="32"/>
    </div>
  </template>
</template>

<script setup lang="ts">
  import Plyr from 'plyr';
  import { computed, onBeforeUnmount, onMounted } from 'vue';
  import MediaContext2 from '@/general/models/MediaContext2';
  import MediaFile2 from '@/general/models/MediaFile2';
  import Modal from '@/general/utils/Modal';
  import CIcon from '@/shared/components/CIcon.vue';
  import CLoader from '@/shared/components/CLoader.vue';
  import CButton from '@/shared/components/buttons/CButton.vue';
  import MediaFileType from '@/shared/constants/MediaFileType';
  import { elementRef } from '@/shared/utils/component';
  import { present } from '@/shared/utils/typescript';

  const ImageModal2 = () => import('@/shared/modals/ImageModal2.vue');

  const props = defineProps<{
    file: MediaFile2;
    files?: Array<MediaFile2>;
    autoplay?: boolean;
    limitedControls?: boolean;
    context?: MediaContext2;
  }>();

  const playerEl = elementRef<HTMLElement>();

  const controls = computed(() => {
    if (props.limitedControls) {
      return ['play', 'fullscreen'];
    } else {
      return ['play', 'progress', props.file.type === MediaFileType.VIDEO ? 'mute' : '', 'download', 'fullscreen'];
    }
  });

  onMounted(() => {
    if (playerEl.value) {
      const player = new Plyr(playerEl.value, {
        controls: controls.value,
        autoplay: props.autoplay,
      });

      onBeforeUnmount(() => player.destroy());
    }
  });

  function openImage() {
    Modal.open(ImageModal2, { file: props.file, context: props.context, imageFiles: present(props.files).filter(f => f.type === MediaFileType.IMAGE) });
  }
</script>

<style lang="scss">
  .c-media-file {
    &-thumbnail {
      overflow: hidden;
      border-radius: $border-radius;
    }

    &-audio, &-video {
      --plyr-color-main: rgba(0, 0, 0, .05);
      --plyr-control-icon-size: 0.75rem;
      --plyr-control-spacing: 0.5rem;
      --plyr-audio-controls-background: transparent;
      --plyr-audio-control-color: $main-fg;
    }

    .plyr {
      min-width: 0;
    }

    .plyr--audio .plyr__controls {
      padding: 0;
    }
  }
</style>
